import { useState, useEffect } from 'react';
import { shouldLaunchSameTab } from '../lib/surveyTabHandle';

const CHANNEL_NAME = 'survey-result';

export type SurveyResultType = 'complete' | 'screenout' | 'quotafull' | 'security-terminate';

export interface SurveyResultMessage {
  type: SurveyResultType;
  points?: number;
  consolation?: number;
}

/**
 * Hook for survey result pages (opened in the survey tab).
 * After a short delay, broadcasts the result to the original tab
 * and attempts to close this tab. Shows a fallback button if blocked.
 */
export function useSurveyTabAutoClose(result: SurveyResultMessage, delayMs = 2000, blocked = false) {
  const [showCloseButton, setShowCloseButton] = useState(false);

  useEffect(() => {
    // Broadcast result to the original tab
    try {
      const channel = new BroadcastChannel(CHANNEL_NAME);
      channel.postMessage(result);
      channel.close();
    } catch {
      // BroadcastChannel not supported — original tab won't get the message
    }

    // Don't start timer while blocked (e.g. push prompt showing)
    if (blocked) return;

    // Mobile / in-app launches navigate the SAME tab to the survey (T1), so
    // this result page loads in the member's main app tab — there is no
    // script-opened tab to close. Skip the close timer and let the page's own
    // in-app CTAs + auto-redirect countdown take over (showCloseButton stays
    // false, so the misleading "Close this tab" affordance never appears).
    if (shouldLaunchSameTab()) return;

    const timer = setTimeout(() => {
      window.close();
      // If window.close() was blocked by the browser, show fallback button
      // We detect this by checking if we're still here after a short delay
      setTimeout(() => setShowCloseButton(true), 300);
    }, delayMs);

    return () => clearTimeout(timer);
  }, [blocked]);

  return { showCloseButton };
}

/**
 * Hook for the original tab (SurveysPage) to listen for survey results
 * broadcast from the survey tab.
 */
export function useSurveyResultListener(
  onResult: (msg: SurveyResultMessage) => void,
) {
  useEffect(() => {
    let channel: BroadcastChannel;
    try {
      channel = new BroadcastChannel(CHANNEL_NAME);
      channel.onmessage = (event: MessageEvent<SurveyResultMessage>) => {
        onResult(event.data);
      };
    } catch {
      // BroadcastChannel not supported
      return;
    }
    return () => channel.close();
  }, [onResult]);
}
